import React, {useState, useEffect} from "react";
import {useRouter} from "next/navigation";
import Link from "next/link";
import _ from "lodash";

import {$store} from "@isudio/globals";
import {cn, useRxSync} from "@isudio/utils";
import {replaceLinks, renderTable} from "./";
import type {IUser} from "@isudio/data";
import {LoadingSpinner, CaseCard, RowWrapper, RowFlex, StringList, JobForm} from "../components";

export const renderContent = (
  content: any[],
  sectionGap: number = 8,
  contentStyle: string = ''
) => {
  const router = useRouter();
  const team$: IUser[] | null = useRxSync($store.Data.team$)
  const [team, setTeam] = useState<IUser[] | null>(team$);
  const [loading, setLoading] = useState(false);

  const onSetLoading = (loading: boolean) => {
    setLoading(loading);
  };

  useEffect(() => {
    if (!team$) return;
    setTeam(_.sortBy(_.cloneDeep(team$), "socialID") as IUser[]);
  }, [team$]);

  return (
    content && content.map((block: any, index: number ) => {
      const expert = (block.expert ? _.find(team,  {socialID: block.expert}) : null);

      if (block.type === "paragraph") {
        const content = (block.text || block.content) ? replaceLinks(block.text || block.content, false) : null;
        return (
          <div
            style={
              block.line && block.line === true ? {
                borderTop: "1px solid #525252",
                paddingTop: `8px`
              } : undefined
            }
          >
            {block.heading && (
              <div
                className="w-fit flex items-center justify-center group"
                style={{
                  paddingTop: (index === 0 ? 0 : `${sectionGap * 0.25}rem`)
                }}
                onClick={block.link ? () => router.push(block.link) : undefined}
              >
                {block.icon && (
                  <div
                    className="w-10">
                    <i
                      className={cn(
                        `fa-duotone fa-light ${block.icon} text-2xl text-custom-gray-600 dark:text-pale-white`,
                        block.link ? `cursor-pointer [@media(hover:none)]:text-generic-hover [@media(hover:hover)]:group-hover:text-generic-hover` : ``
                      )}
                    />
                  </div>
                )}
                <div>
                  <h1
                    className={cn(
                    `text-md md:text-xl font-black text-black dark:text-white`,
                      block.link ? `cursor-pointer [@media(hover:none)]:text-generic-hover [@media(hover:hover)]:group-hover:text-generic-hover` : ``
                    )}
                  >
                    {block.heading}
                  </h1>
                </div>
              </div>
            )}

            {block.caption && (
              <div
                className="w-fit pt-2">
                <h2
                  className="text-sm md:text-md font-extrabold leading-normal text-custom-gray-700 dark:text-ghost-white">
                  {block.caption}
                </h2>
              </div>
            )}

            {block.bullet && (
              <div
                className="w-fit pt-2">
                <h3
                  className="text-sm md:text-md font-semibold leading-normal text-custom-gray-700 dark:text-ghost-white">
                  {block.bullet}
                </h3>
              </div>
            )}

            {block.emphasis && (
              <div
                className="w-fit pt-2">
                <em
                  className="text-sm md:text-md font-semibold leading-normal text-custom-gray-700 dark:text-ghost-white">
                  {block.emphasis}
                </em>
              </div>
            )}

            {block.expert && (
              <div className="w-fit pt-2 cursor-pointer group">
                <RowWrapper
                  className="row-wrapper flex w-full">
                  <RowFlex
                    className="gap-4">
                    <div
                      className="flex shrink-0">
                        <Link key={block.expert} href={`/team/${block.expert}`}>
                          <div className="relative w-fit h-40 sm:h-48 xl:h-56 bg-[radial-gradient(circle,#ffffff_0%,#c2c2c2_100%)]">
                            <img
                              src={`/assets/img/photos/bw/${block.expert}.png`}
                              alt={`${block.expert}`}
                              className="w-auto h-40 sm:h-48 xl:h-56 object-cover transition-opacity duration-300 [@media(hover:hover)]:group-hover:opacity-0 [@media(hover:none)]:opacity-0"
                            />
                            <img
                              src={`/assets/img/photos/co/${block.expert}.png`}
                              alt={`${expert?.firstName}`}
                              className="absolute inset-0 w-auto h-40 sm:h-48 xl:h-56 object-cover transition-opacity duration-300 opacity-0 [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:none)]:opacity-100"
                            />
                          </div>
                        </Link>
                    </div>

                    <div
                      className="flex flex-col flex-1 min-w-0 gap-1">
                      <Link key={block.expert} href={`/team/${block.expert}`}>
                        <h2 className="w-fit text-md md:text-lg font-semibold text-center text-black dark:text-white [@media(hover:none)]:text-generic-hover [@media(hover:hover)]:group-hover:text-generic-hover">
                          {`${expert?.firstName}`}
                        </h2>
                        {block?.profile}
                      </Link>
                    </div>
                  </RowFlex>
                </RowWrapper>
              </div>
            )}

            {content && (
              <p
                className={cn(`w-fit pt-2 text-sm md:text-md font-normal leading-normal text-custom-gray-600 dark:text-pale-white`, contentStyle)}>
                {!block.heading && block.link ?
                  <Link href={block.link} className="underline cursor-pointer [@media(hover:none)]:text-generic-hover [@media(hover:hover)]:hover:text-generic-hover">
                    {content}
                  </Link>
                : content}
              </p>
            )}

            {block.cases && (
              <div
                className="case-wrapper mt-4 gap-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {block.cases.map((workCase: any) => (
                  <CaseCard workCase={workCase}></CaseCard>
                ))}
              </div>
            )}

            {block.books && (
              <div className="books-wrapper flex flex-col gap-12 max-w-5xl mx-auto p-4">
                {block.books.map((book: any, index: number) => (
                  <div
                    key={index}
                    className="flex flex-col md:flex-row gap-8 items-start md:items-center py-6"
                  >
                    {/* Book Cover with Deep Left-Bottom Shadow */}
                    <div className="w-48 max-w-full shrink-0 mx-auto md:mx-0">
                      <img
                        src={book.cover}
                        alt={book.title}
                        className="w-full h-auto object-cover rounded-sm shadow-[-10px_10px_10px_-2px_rgba(0,0,0,1)]"
                      />
                    </div>

                    {/* Book Information */}
                    <div className="flex flex-col flex-1 gap-4 font-sans text-left">
                      {/* Title */}
                      <h2 className="text-xl md:text-2xl font-bold tracking-wide text-black dark:text-white uppercase">
                        {book.title}
                      </h2>

                      {/* Description */}
                      <p className="text-base md:text-lg italic font-light leading-relaxed max-w-3xl text-custom-gray-600 dark:text-pale-white">
                        {book.text}
                      </p>

                      {/* Publication Info */}
                      <p className="text-sm md:text-base font-light leading-relaxed max-w-3xl text-custom-gray-600 dark:text-pale-white">
                        {book.published}
                      </p>

                      {/* Buy Button (Only renders if 'buy' property exists) */}
                      {book.buy && (
                        <div className="mt-2">
                          <a
                            href={book.buy}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="
                              inline-block font-medium px-8 py-2.5 text-sm tracking-wider uppercase shadow-md rounded-full transition duration-200
                              text-white bg-custom-blue-700
                              [@media(hover:hover)]:text-ghost-white
                              [@media(hover:hover)]:hover:scale-105
                              [@media(hover:hover)]:hover:text-white
                              [@media(hover:hover)]:hover:bg-custom-blue-500
                            "
                          >
                            BUY
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {block.stringList && (
              <div
                className="w-fit pt-2 pl-2 text-sm md:text-md font-normal leading-normal text-custom-gray-600 dark:text-pale-white">
                <StringList items={block.stringList}></StringList>
              </div>
            )}
          </div>
        );
      }

      if (block.type === 'table') {
        return renderTable(block);
      }

      if (block.type === 'job') {
        return (
          <div
            className="w-fit flex flex-col"
            style={{
              paddingTop: (index === 0 ? 0 : `${sectionGap * 0.25}rem`)
            }}
          >
            <div>
              <h1
                className="text-md md:text-xl font-black text-black dark:text-white">
                {block.heading}
              </h1>
            </div>

            {block.jobs && block.jobs.map((job: any) => {
              return (
                <div
                  className="w-fit">
                  <h2
                    className="py-2 text-sm md:text-md font-extrabold leading-normal text-custom-gray-700 dark:text-ghost-white">
                    {job.jobTitle}
                  </h2>

                  {renderTable(job.table)}

                  <div className="relative w-fit mt-2 p-3  bg-blue-500/5 rounded border border-blue-500/20">
                    {loading ?
                      <LoadingSpinner
                        className="absolute"
                      />
                    : null}

                    {job.form?.heading && (
                      <h3 className="text-sm md:text-md font-bold text-black dark:text-white mb-1">
                        {job.form.heading}
                      </h3>
                    )}
                    {job.form?.text && (
                      <p className="text-sm text-custom-gray-600 dark:text-pale-white italic">
                        {job.form.text}
                      </p>
                    )}
                    <JobForm
                      jobId={job.jobId}
                      onSetLoading={onSetLoading}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        );
      }

      if (block.type === 'list') {
        const style = (block as any).style || 'disc'

        let listClass = 'pl-5 pt-2 gap-2 text-sm md:text-md font-normal leading-normal text-custom-gray-600 dark:text-pale-white'

        switch (style) {
          case 'number':
            listClass = 'list-decimal ' + listClass
            break
          case 'circle':
            listClass = 'list-[style:circle] ' + listClass
            break
          case 'dash':
            listClass = "list-[style:'–'] " + listClass
            break
          case 'none':
            listClass = 'list-none ' + listClass
            break
          default:
            listClass = 'list-disc ' + listClass
        }

        return (
          <div>
            <ul
              className={listClass}>
              {block.items.map((item: any, idx: number) => (
                <li
                  key={idx}>{item}</li>
              ))}
            </ul>
          </div>
        )
      }
      return null;
    })
  );
};